import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import {
  useGetLandingCreatorQuery,
  useGetPublicCreatorQuery,
} from "../../app/services/publicCreators";
import FollowButton from "../../components/follow-button/FollowButton";
import { getAuthTokenFromCookies } from "../../common/utils";
import ProfileKeyVisual from "./components/ProfileKeyVisual";
import ProfileStatement from "./components/ProfileStatement";
import ProfileMediaSection from "./components/ProfileMediaSection";
import ProfileReviews from "./components/ProfileReviews";
import ProfileTextSection from "./components/ProfileTextSection";
import ProfileFooterSection from "./components/ProfileFooterSection";
import { pickLang } from "./utils";
import type { ProfileLanguage } from "../../types/profile";

export interface ProfileProps {
  /** RootSegmentDispatcher から渡される slug。ルート（/profile/:slug）経由では useParams から取得。 */
  slug?: string;
  /**
   * サイトトップ（soremo.jp/）表示時に true。slug の代わりに
   * `GET /api/landing-profile`（ランディングアカウント解決）で取得する。
   */
  landing?: boolean;
}

/**
 * クリエイター公開プロフィールページ（slug アクセスの読み取り専用ビューア）。
 *
 * 構成: キービジュアル → フォロー/見出し → ステートメント → メディア →
 * レビュー → テキスト → フッター。BE `GET /api/creators/<slug>` の合成
 * レスポンスを 1 回取得して描画する（landing 時は `GET /api/landing-profile`）。
 */
const Profile: React.FC<ProfileProps> = ({ slug: slugProp, landing = false }) => {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug ?? "";
  const [language, setLanguage] = useState<ProfileLanguage>("JP");

  // フックは条件分岐できないため、両クエリを skip 付きで呼び分ける。
  const slugResult = useGetPublicCreatorQuery(slug, {
    skip: landing || !slug,
  });
  const landingResult = useGetLandingCreatorQuery(undefined, {
    skip: !landing,
  });
  const { data, isLoading, isError } = landing ? landingResult : slugResult;
  const profile = data?.data;

  // 本人判定用に現在ユーザーを取得。未ログイン環境（公開ページ）では API を叩かない。
  const { data: currentUserData } = useGetCurrentUserQuery(undefined, {
    skip: !getAuthTokenFromCookies(),
  });
  const currentUserId: number | undefined = currentUserData?.data?.id;
  const isSelf = !!profile && currentUserId !== undefined && currentUserId === profile.user_id;

  const displayName = profile
    ? pickLang(profile.display_name_jp, profile.display_name_en, language)
    : "";
  const title = profile ? pickLang(profile.title_jp, profile.title_en, language) : "";

  // クライアント描画時のタイトル更新（クローラ向け OGP は Lambda@Edge が担当）。
  useEffect(() => {
    if (displayName) {
      document.title = `${displayName} | SOREMO`;
    }
    return () => {
      document.title = "SOREMO";
    };
  }, [displayName]);

  if (isLoading) {
    return (
      <div className="u-col-center u-w100" style={{ minHeight: "60vh" }}>
        <span className="bodytext-13 u-text-light-gray">読み込み中...</span>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="u-col-center u-w100 u-gap8" style={{ minHeight: "60vh" }}>
        <span className="heading-spacing-18">ページが見つかりません</span>
        <span className="bodytext-13 u-text-light-gray">
          公開されていないか、URL が正しくない可能性があります。
        </span>
      </div>
    );
  }

  return (
    <div className="u-w100 u-col">
      {profile.header && <ProfileKeyVisual header={profile.header} />}

      <div
        style={{
          width: "100%",
          maxWidth: 1140,
          margin: "0 auto",
          padding: "0 16px",
        }}
        className="u-col"
      >
        {/* 見出し: 名前 + 肩書き（横並び） + フォロー（本人は非表示） */}
        <section className="u-row-between u-w100 u-ptb16 u-gap24">
          <div className="u-row u-gap24" style={{ flexWrap: "wrap", alignItems: "baseline" }}>
            <span className="heading-32">{displayName}</span>
            {title && <span className="bodytext-16">{title}</span>}
          </div>
          {!isSelf && (
            <FollowButton
              userId={profile.user_id}
              isFollowing={profile.is_following}
              displayName={displayName}
              variant="bookmark"
            />
          )}
        </section>

        {profile.statement && (
          <ProfileStatement
            statement={profile.statement}
            displayName={displayName}
            title={title}
            avatarUrl={profile.avatar_url}
            language={language}
            fitViewport
          />
        )}

        <ProfileMediaSection
          works={profile.works}
          filterTags={profile.work_filter_tags}
          creatorName={displayName}
          // landing 時は URL に slug が無いため、レスポンス内の vanity_slug を使う。
          creatorSlug={profile.vanity_slug || slug}
          layout={profile.work_display_layout}
        />

        <ProfileReviews reviews={profile.reviews} />

        <ProfileTextSection blocks={profile.profile_texts} language={language} />
      </div>

      {/* フッターは全幅（#fcfcfc）。メニュー等が空でも既定で表示する。 */}
      <ProfileFooterSection
        footer={profile.footer}
        language={language}
        onChangeLanguage={setLanguage}
      />
    </div>
  );
};

export default Profile;
